/* =========================================================
   FILE: cn-theme-tabs.js
   CARDCOMPLEX Theme-Tabs + Produkt-Slider
   Bereinigt: keine Raritäts-DOM-Manipulation mehr.
   Raritäts-Badges werden ausschließlich in cn-product-rarity-badges.js behandelt.
========================================================= */

(() => {
  'use strict';

  if (window.__cnThemeTabsLoaded) return;
  window.__cnThemeTabsLoaded = true;

  const BLOCK_SELECTOR = "[data-ygo-theme-deck-block]";
  const TAB_SELECTOR = "[data-ygo-theme-tab]";
  const PANEL_SELECTOR = "[data-ygo-theme-panel]";
  const TABS_SELECTOR = ".ygo-theme-tabs";
  const TABS_SCROLLER_SELECTOR = ".ygo-theme-tabs__list";
  const SLIDER_SELECTOR = "slider-component[data-ygo-slider]";
  const RAIL_SELECTOR = ".ygo-theme-deck__rail";
  const TITLE_TOUCH_SELECTOR = ".ygo-theme-deck__titles";
  const TITLE_SCROLL_SELECTOR = ".ygo-theme-deck__title";

  const boundScrollers = new WeakSet();
  const scheduledScrollers = new WeakMap();
  const boundTitleTouchAreas = new WeakSet();
  const scheduledTitles = new WeakMap();

  const mobileMedia = window.matchMedia("(max-width: 749px)");

  let resizeFrame = null;

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  function isMobileViewport() {
    return mobileMedia.matches;
  }

  function escapeSelector(value) {
    if (typeof window.CSS?.escape === "function") {
      return CSS.escape(value);
    }

    return String(value).replace(/["\\]/g, "\\$&");
  }

  function getPanel(block, targetId) {
    if (!block || !targetId) return null;

    try {
      return block.querySelector(`#${escapeSelector(targetId)}`);
    } catch (error) {
      return block.querySelector(`[id="${escapeSelector(targetId)}"]`);
    }
  }

  function setBoolAttr(element, name, value) {
    if (!element) return;

    const next = value ? "true" : "false";

    if (element.getAttribute(name) !== next) {
      element.setAttribute(name, next);
    }
  }

  function getSliderRail(slider) {
    if (!slider) return null;

    const railId = slider.getAttribute("data-slider-id");

    if (railId) {
      try {
        const railById = slider.querySelector(`#${escapeSelector(railId)}`);
        if (railById) return railById;
      } catch (error) {}
    }

    return slider.querySelector(RAIL_SELECTOR);
  }

  function updateScrollState(target, scroller) {
    if (!target || !scroller) return;

    const tolerance = 2;
    const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const left = maxScroll > 0 ? clamp(scroller.scrollLeft, 0, maxScroll) : 0;
    const isScrollable = maxScroll > tolerance;

    setBoolAttr(target, "data-can-scroll-left", isScrollable && left > tolerance);
    setBoolAttr(
      target,
      "data-can-scroll-right",
      isScrollable && left < maxScroll - tolerance
    );
  }

  function scheduleScrollState(target, scroller) {
    if (!target || !scroller) return;

    if (scheduledScrollers.has(scroller)) return;

    const frame = requestAnimationFrame(() => {
      scheduledScrollers.delete(scroller);
      updateScrollState(target, scroller);
    });

    scheduledScrollers.set(scroller, frame);
  }

  function bindScrollState(target, scroller) {
    if (!target || !scroller || boundScrollers.has(scroller)) return;

    boundScrollers.add(scroller);

    scroller.addEventListener(
      "scroll",
      () => scheduleScrollState(target, scroller),
      { passive: true }
    );
  }

  function bindScrollStates(scope = document) {
    scope.querySelectorAll(TABS_SELECTOR).forEach((wrapper) => {
      const scroller = wrapper.querySelector(TABS_SCROLLER_SELECTOR);
      if (!scroller) return;

      bindScrollState(wrapper, scroller);
    });

    scope.querySelectorAll(SLIDER_SELECTOR).forEach((slider) => {
      const rail = getSliderRail(slider);
      if (!rail) return;

      bindScrollState(slider, rail);
    });
  }

  function refreshScrollStates(scope = document) {
    scope.querySelectorAll(TABS_SELECTOR).forEach((wrapper) => {
      const scroller = wrapper.querySelector(TABS_SCROLLER_SELECTOR);
      if (!scroller) return;

      scheduleScrollState(wrapper, scroller);
    });

    scope.querySelectorAll(SLIDER_SELECTOR).forEach((slider) => {
      const rail = getSliderRail(slider);
      if (!rail) return;

      scheduleScrollState(slider, rail);
    });
  }

  function refreshInitialMobileScrollStates(scope = document) {
    if (!isMobileViewport()) return;

    /*
      Initialer Mobile-Fade:
      - läuft nur auf Mobile
      - erst nach dem ersten Render
      - misst nur scrollWidth/clientWidth
      - sorgt dafür, dass der rechte Fade direkt beim Öffnen sichtbar ist
    */
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        refreshScrollStates(scope);
      });
    });

    /*
      Falls Fonts/CSS minimal später greifen und sich Pill-Breiten ändern,
      korrigieren wir genau einmal nach.
    */
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready
        .then(() => {
          if (isMobileViewport()) {
            refreshScrollStates(scope);
          }
        })
        .catch(() => {});
    }
  }

  function resetRails(panel) {
    if (!panel) return;

    panel.querySelectorAll(RAIL_SELECTOR).forEach((rail) => {
      rail.scrollLeft = 0;
    });
  }

  function activateTab(block, targetId, options = {}) {
    if (!block || !targetId) return;

    const targetPanel = getPanel(block, targetId);
    if (!targetPanel) return;

    const shouldResetRails = options.resetRails !== false;

    const tabs = Array.from(block.querySelectorAll(TAB_SELECTOR));
    const panels = Array.from(block.querySelectorAll(PANEL_SELECTOR));

    panels.forEach((panel) => {
      const isActive = panel === targetPanel;

      panel.hidden = !isActive;
      panel.setAttribute("aria-hidden", isActive ? "false" : "true");
    });

    tabs.forEach((tab) => {
      const tabTarget = tab.getAttribute("data-target") || tab.getAttribute("aria-controls");
      const isActive = tabTarget === targetId;

      tab.classList.toggle("is-active", isActive);
      tab.setAttribute("aria-selected", isActive ? "true" : "false");
      tab.tabIndex = isActive ? 0 : -1;
    });

    if (shouldResetRails) {
      resetRails(targetPanel);
    }

    if (options.refreshScroll === true) {
      requestAnimationFrame(() => {
        bindScrollStates(targetPanel);
        bindTitleTouchAreas(targetPanel);
        refreshScrollStates(block);
        refreshTitleScrollStates(targetPanel);
      });
    }
  }

  function initBlock(block) {
    if (!block || block.dataset.cnThemeTabsReady === "true") return;

    const tabs = Array.from(block.querySelectorAll(TAB_SELECTOR));
    const panels = Array.from(block.querySelectorAll(PANEL_SELECTOR));

    if (!panels.length) return;

    block.dataset.cnThemeTabsReady = "true";

    const selectedTab = tabs.find((tab) => tab.getAttribute("aria-selected") === "true");
    const selectedPanel = panels.find(
      (panel) => !panel.hidden && panel.getAttribute("aria-hidden") !== "true"
    );

    if (selectedTab) {
      const targetId = selectedTab.getAttribute("data-target") || selectedTab.getAttribute("aria-controls");

      activateTab(block, targetId, {
        resetRails: false,
        refreshScroll: false
      });

      return;
    }

    if (selectedPanel && selectedPanel.id) {
      activateTab(block, selectedPanel.id, {
        resetRails: false,
        refreshScroll: false
      });

      return;
    }

    if (tabs[0]) {
      const targetId = tabs[0].getAttribute("data-target") || tabs[0].getAttribute("aria-controls");

      activateTab(block, targetId, {
        resetRails: false,
        refreshScroll: false
      });

      return;
    }

    panels.forEach((panel, index) => {
      const isActive = index === 0;

      panel.hidden = !isActive;
      panel.setAttribute("aria-hidden", isActive ? "false" : "true");
    });
  }

  function initThemeBlocks(scope = document) {
    scope.querySelectorAll(BLOCK_SELECTOR).forEach(initBlock);
  }

  const AUTO_CONFIG_SELECTOR = "[data-cn-auto-reprint-config]";
  const AUTO_BLOCK_SELECTOR = "[data-cn-auto-reprint-block]";
  const AUTO_PANEL_SELECTOR = "[data-cn-auto-reprint-panel]";
  const AUTO_RAIL_SELECTOR = "[data-cn-auto-reprint-rail]";
  const AUTO_TAB_TEMPLATE_SELECTOR = "[data-cn-auto-reprint-tab-template]";
  const AUTO_TAB_SELECTOR = "[data-cn-auto-reprint-tab]";
  const CARD_NAME_HISTORY_SELECTOR = "[data-cn-card-name-history]";
  const AUTO_SEARCH_SECTION = "cn-auto-reprints";

  const normalizeProductUrl = (value) => {
    try {
      const url = new URL(value || "", window.location.origin);
      return url.pathname.replace(/\/+$/, "");
    } catch (error) {
      return String(value || "").split("?")[0].replace(/\/+$/, "");
    }
  };

  function getCardNameHistory(config) {
    const directValue = config.dataset.cardNameHistory;
    if (directValue) return directValue;

    const productScope = config.closest("product-info") || document;
    const historyElement =
      productScope.querySelector(CARD_NAME_HISTORY_SELECTOR) ||
      (productScope === document
        ? null
        : document.querySelector(CARD_NAME_HISTORY_SELECTOR));

    return historyElement?.dataset.cnCardNameHistory || "";
  }

  function normalizeHistoricalCardName(value) {
    return String(value || "")
      .replace(/^\s*(?:old|new)\s*::\s*/i, "")
      .trim();
  }

  function splitHistoricalCardNames(value) {
    return String(value || "")
      .split(/\r?\n|[;,|]+/)
      .map(normalizeHistoricalCardName)
      .filter(Boolean);
  }

  function getAutomaticReprintNames(config) {
    const names = [];
    const seen = new Set();

    const addName = (value) => {
      const name = normalizeHistoricalCardName(value);
      const key = name.toLocaleLowerCase("de-DE");

      if (!name || seen.has(key)) return;

      seen.add(key);
      names.push(name);
    };

    addName(config.dataset.cardName);
    splitHistoricalCardNames(getCardNameHistory(config)).forEach(addName);

    return names;
  }

  async function fetchAutomaticReprintName(cardName) {
    const params = new URLSearchParams({
      q: cardName,
      type: "product",
      section_id: AUTO_SEARCH_SECTION
    });

    const searchUrl = window.routes?.search_url || '/search';
    const separator = searchUrl.includes('?') ? '&' : '?';

    const response = await fetch(`${searchUrl}${separator}${params.toString()}`, {
      credentials: "same-origin",
      headers: { Accept: "text/html" }
    });

    if (!response.ok) {
      throw new Error("Automatische Reprint-Suche konnte nicht geladen werden.");
    }

    const html = await response.text();
    const parsed = new DOMParser().parseFromString(html, "text/html");
    const template = parsed.querySelector("[data-cn-auto-reprints-result]");
    if (!template) return [];

    const source = template.content || template;
    return Array.from(source.querySelectorAll("[data-cn-auto-reprint-product]"));
  }

  function getExistingReprintKeys(rail) {
    const keys = new Set();

    if (!rail) return keys;

    rail
      .querySelectorAll("[data-ygo-theme-product-slide], [data-cn-auto-reprint-product]")
      .forEach((item) => {
        const productId = String(item.dataset.productId || "");
        const productUrl = normalizeProductUrl(
          item.dataset.productUrl ||
          item.querySelector("a[href*='/products/']")?.getAttribute("href") ||
          ""
        );

        if (productId) keys.add(`id:${productId}`);
        if (productUrl) keys.add(`url:${productUrl}`);
      });

    return keys;
  }

  function getReprintTarget(block, config, preferRenderedPanel = false) {
    if (!block) return { panel: null, rail: null };

    let panel = null;

    /*
      Wenn der Reprint-Tab bereits serverseitig existiert, ist dessen Panel
      das Ziel. Das leere Auto-Reprint-Fallback darf in diesem Fall nicht
      bevorzugt werden.
    */
    if (preferRenderedPanel) {
      const reprintTab = Array.from(block.querySelectorAll(TAB_SELECTOR)).find(
        (tab) =>
          tab.matches(AUTO_TAB_SELECTOR) ||
          /\breprints?\b/i.test(tab.textContent || "")
      );

      const renderedPanelId =
        reprintTab?.getAttribute("data-target") ||
        reprintTab?.getAttribute("aria-controls");

      panel = getPanel(block, renderedPanelId);
    }

    if (!panel && config?.dataset.panelId) {
      panel = getPanel(block, config.dataset.panelId);
    }

    if (!panel) {
      panel = block.querySelector(AUTO_PANEL_SELECTOR);
    }

    const rail =
      panel?.querySelector(AUTO_RAIL_SELECTOR) ||
      panel?.querySelector(RAIL_SELECTOR) ||
      null;

    return { panel, rail };
  }

  async function fetchAutomaticReprints(config, rail, limit = 20) {
    const initialNames = getAutomaticReprintNames(config);

    if (!initialNames.length || limit < 1) {
      return [];
    }

    const currentId = String(config.dataset.currentProductId || "");
    const currentUrl = normalizeProductUrl(
      config.dataset.currentProductUrl || window.location.pathname
    );

    /*
      Enthält sowohl bereits serverseitig gerenderte Karten als auch
      Karten, die während dieses Suchlaufs hinzugefügt werden.
    */
    const seenProducts = getExistingReprintKeys(rail);

    /*
      Bereits gesuchte oder vorgemerkte Kartennamen.
      Dadurch entstehen keine Endlosschleifen zwischen altem und neuem Namen.
    */
    const knownNames = new Set();
    const searchQueue = [];

    const queueName = (value) => {
      const name = normalizeHistoricalCardName(value);
      const key = name.toLocaleLowerCase("de-DE");

      if (!name || knownNames.has(key)) return;

      knownNames.add(key);
      searchQueue.push(name);
    };

    initialNames.forEach(queueName);

    const items = [];

    /*
      Sicherheitslimit für miteinander verknüpfte Namensänderungen.
      Für normale Karten werden nur ein oder zwei Suchläufe benötigt.
    */
    const maximumSearches = 10;
    let completedSearches = 0;

    while (
      searchQueue.length &&
      completedSearches < maximumSearches &&
      items.length < limit
    ) {
      const cardName = searchQueue.shift();
      completedSearches += 1;

      const searchItems = await fetchAutomaticReprintName(cardName);

      for (const item of searchItems) {
        /*
          Historische Namen jedes gefundenen Produkts ebenfalls durchsuchen.

          Dadurch funktioniert die Verknüpfung in beide Richtungen:
          aktueller Name -> alter Name
          alter Name -> aktueller Name
        */
        splitHistoricalCardNames(item.dataset.cardNameHistory).forEach(queueName);

        const productId = String(item.dataset.productId || "");
        const productUrl = normalizeProductUrl(
          item.dataset.productUrl ||
            item.querySelector("a[href*='/products/']")?.getAttribute("href") ||
            ""
        );

        /*
          Das aktuell geöffnete Produkt niemals als eigenen Reprint anzeigen.
        */
        if (
          (currentId && productId === currentId) ||
          (currentUrl && productUrl === currentUrl)
        ) {
          continue;
        }

        const idKey = productId ? `id:${productId}` : "";
        const urlKey = productUrl ? `url:${productUrl}` : "";

        if (!idKey && !urlKey) {
          continue;
        }

        /*
          Bereits serverseitig vorhandene Produkte werden nur einzeln
          übersprungen. Die komplette Suchgruppe bleibt erhalten.
        */
        if (
          (idKey && seenProducts.has(idKey)) ||
          (urlKey && seenProducts.has(urlKey))
        ) {
          continue;
        }

        if (idKey) seenProducts.add(idKey);
        if (urlKey) seenProducts.add(urlKey);

        items.push(item);

        if (items.length >= limit) {
          break;
        }
      }
    }

    return items;
  }

  function appendAutomaticReprints(block, items, target) {
    const panel = target?.panel || null;
    const rail = target?.rail || null;

    if (!panel || !rail || !items.length) return false;

    items.forEach((item) => rail.appendChild(item));

    const slider = panel.querySelector(SLIDER_SELECTOR);

    if (slider) {
      bindScrollState(slider, rail);
      refreshScrollStates(panel);
    }

    document.dispatchEvent(
      new CustomEvent("cn:auto-reprints:loaded", {
        detail: {
          block,
          count: rail.querySelectorAll("[data-ygo-theme-product-slide]").length
        }
      })
    );

    return true;
  }

  function ensureTabsUi(block, config, panel) {
    let tabs = block.querySelector(TABS_SELECTOR);
    let list = tabs?.querySelector(TABS_SCROLLER_SELECTOR);

    if (!tabs || !list) {
      tabs = document.createElement("div");
      tabs.className = "ygo-theme-tabs";
      tabs.setAttribute("role", "tablist");
      tabs.setAttribute("aria-label", "Zugehörigkeiten dieser Karte");
      tabs.setAttribute("data-can-scroll-left", "false");
      tabs.setAttribute("data-can-scroll-right", "false");

      const label = document.createElement("div");
      label.className = "ygo-theme-tabs__label";
      label.textContent = "Zugehörigkeit dieser Karte";

      list = document.createElement("div");
      list.className = "ygo-theme-tabs__list";

      tabs.append(label, list);
      block.insertBefore(tabs, block.firstChild);
    }

    let tab = block.querySelector("[data-cn-auto-reprint-tab]");
    if (!tab) {
      const template = block.querySelector(AUTO_TAB_TEMPLATE_SELECTOR);
      const fragment = template?.content?.cloneNode(true);
      tab = fragment?.querySelector?.("[data-cn-auto-reprint-tab]") || null;

      if (!tab) return null;
      list.appendChild(tab);
    }

    const panelId = config.dataset.panelId || `YgoThemePanel-auto-${Date.now()}`;
    panel.id = panelId;
    tab.setAttribute("aria-controls", panelId);
    tab.setAttribute("data-target", panelId);

    return { tabs, list, tab };
  }

  function revealAutomaticReprints(block, config, items) {
    const { panel, rail } = getReprintTarget(block, config, false);

    if (!panel || !rail || !items.length) return false;

    items.forEach((item) => rail.appendChild(item));

    const existingPanels = Array.from(
      block.querySelectorAll(PANEL_SELECTOR)
    ).filter((item) => item !== panel);

    const hasOtherPanels = existingPanels.length > 0;

    block.hidden = false;

    /*
      Nur Reprints vorhanden:
      Keine Überschrift und keinen unnötigen einzelnen Reprint-Tab erzeugen.
    */
    if (!hasOtherPanels) {
      block
        .querySelectorAll(TABS_SELECTOR)
        .forEach((tabs) => tabs.remove());

      panel.hidden = false;
      panel.setAttribute("aria-hidden", "false");

      block.classList.remove("is-single", "is-multi");
      block.classList.add("is-reprint-only");
      block.dataset.themeCount = "1";
    } else {
      const ui = ensureTabsUi(block, config, panel);
      if (!ui) return false;

      const totalTabs = block.querySelectorAll(TAB_SELECTOR).length;

      block.classList.remove("is-reprint-only");
      block.classList.toggle("is-single", totalTabs === 1);
      block.classList.toggle("is-multi", totalTabs > 1);
      block.dataset.themeCount = String(totalTabs);

      const label = ui.tabs.querySelector(".ygo-theme-tabs__label");

      if (label) {
        label.textContent =
          totalTabs === 1
            ? "Zugehörigkeit dieser Karte"
            : "Zugehörigkeiten dieser Karte";
      }

      panel.hidden = true;
      panel.setAttribute("aria-hidden", "true");

      ui.tab.setAttribute("aria-selected", "false");
      ui.tab.tabIndex = -1;
      ui.tab.classList.remove("is-active");
    }

    const slider = panel.querySelector(SLIDER_SELECTOR);

    if (slider) {
      slider.setAttribute("data-can-scroll-left", "false");
      slider.setAttribute(
        "data-can-scroll-right",
        items.length > 1 ? "true" : "false"
      );
    }

    const productInfo = block.closest("product-info");

    if (productInfo) {
      productInfo.classList.remove("ygo-no-theme-deck");
      productInfo.classList.add("ygo-has-theme-deck");
    }

    block.removeAttribute("data-cn-theme-tabs-ready");

    initBlock(block);
    bindScrollStates(block);
    refreshScrollStates(block);

    document.dispatchEvent(
      new CustomEvent("cn:auto-reprints:loaded", {
        detail: {
          block,
          count: items.length
        }
      })
    );

    return true;
  }

  async function initAutomaticReprints(scope = document) {
    const configs = Array.from(scope.querySelectorAll(AUTO_CONFIG_SELECTOR));

    await Promise.all(configs.map(async (config) => {
      if (config.dataset.cnAutoReprintsReady === "true") return;
      config.dataset.cnAutoReprintsReady = "true";

      const block = config.closest(AUTO_BLOCK_SELECTOR);
      if (!block) return;

      const serverCount = Number(config.dataset.serverReprintCount || 0);
      const target = getReprintTarget(block, config, serverCount > 0);
      const renderedCount = Math.max(
        serverCount,
        target.rail
          ? target.rail.querySelectorAll(
              "[data-ygo-theme-product-slide], [data-cn-auto-reprint-product]"
            ).length
          : 0
      );
      const hasRenderedReprints = renderedCount > 0;
      const availableSlots = Math.max(0, 20 - renderedCount);

      try {
        const items = await fetchAutomaticReprints(
          config,
          target.rail,
          availableSlots
        );
        if (!items.length) return;

        if (hasRenderedReprints) {
          appendAutomaticReprints(block, items, target);
        } else {
          revealAutomaticReprints(block, config, items);
        }
      } catch (error) {
        console.warn("[Cardcomplex] Automatische Reprint-Suche:", error);
      }
    }));
  }

  function updateTitleScrollState(title) {
    if (!title) return;

    const maxScroll = Math.max(0, title.scrollWidth - title.clientWidth);
    const left = maxScroll > 0 ? clamp(title.scrollLeft, 0, maxScroll) : 0;
    const tolerance = 2;
    const hasOverflow = maxScroll > tolerance;

    setBoolAttr(
      title,
      "data-can-scroll-left",
      hasOverflow && left > tolerance
    );

    setBoolAttr(
      title,
      "data-can-scroll-right",
      hasOverflow && left < maxScroll - tolerance
    );
  }

  function scheduleTitleScrollState(title) {
    if (!title || scheduledTitles.has(title)) return;

    const frame = requestAnimationFrame(() => {
      scheduledTitles.delete(title);
      updateTitleScrollState(title);
    });

    scheduledTitles.set(title, frame);
  }

  function bindTitleTouchArea(titles) {
    if (!titles || boundTitleTouchAreas.has(titles)) return;

    const title = titles.querySelector(TITLE_SCROLL_SELECTOR);
    if (!title) return;

    boundTitleTouchAreas.add(titles);

    let active = false;
    let horizontalDrag = false;
    let pointerId = null;
    let startX = 0;
    let startY = 0;
    let startScrollLeft = 0;

    const finishDrag = (event) => {
      if (!active && !horizontalDrag) return;

      active = false;
      horizontalDrag = false;

      titles.classList.remove("is-title-dragging");

      try {
        if (
          event?.pointerId != null &&
          titles.hasPointerCapture(event.pointerId)
        ) {
          titles.releasePointerCapture(event.pointerId);
        }
      } catch (error) {}

      pointerId = null;
      scheduleTitleScrollState(title);
    };

    /*
      Funktioniert mit Maus, Trackpad, Stift und Touch.
      Das gesamte Feld aus Titel + Untertitel ist ziehbar.
    */
    titles.addEventListener("pointerdown", (event) => {
      if (
        event.button !== undefined &&
        event.button !== 0
      ) {
        return;
      }

      const maxScroll = Math.max(
        0,
        title.scrollWidth - title.clientWidth
      );

      if (maxScroll <= 2) return;

      active = true;
      horizontalDrag = false;
      pointerId = event.pointerId;

      startX = event.clientX;
      startY = event.clientY;
      startScrollLeft = title.scrollLeft;
    });

    titles.addEventListener(
      "pointermove",
      (event) => {
        if (
          !active ||
          event.pointerId !== pointerId
        ) {
          return;
        }

        const deltaX = event.clientX - startX;
        const deltaY = event.clientY - startY;

        const absoluteX = Math.abs(deltaX);
        const absoluteY = Math.abs(deltaY);

        if (!horizontalDrag) {
          if (
            absoluteY > 8 &&
            absoluteY > absoluteX * 1.15
          ) {
            finishDrag(event);
            return;
          }

          if (
            absoluteX > 5 &&
            absoluteX > absoluteY
          ) {
            horizontalDrag = true;
            titles.classList.add("is-title-dragging");

            try {
              titles.setPointerCapture(event.pointerId);
            } catch (error) {}
          }
        }

        if (!horizontalDrag) return;

        event.preventDefault();

        title.scrollLeft =
          startScrollLeft - deltaX;

        scheduleTitleScrollState(title);
      },
      { passive: false }
    );

    titles.addEventListener("pointerup", finishDrag);
    titles.addEventListener("pointercancel", finishDrag);
    titles.addEventListener(
      "lostpointercapture",
      finishDrag
    );

    /*
      Trackpad horizontal sowie Shift + Mausrad.
      Auch über dem Untertitel wird der Titel bewegt.
    */
    titles.addEventListener(
      "wheel",
      (event) => {
        const maxScroll = Math.max(
          0,
          title.scrollWidth - title.clientWidth
        );

        if (maxScroll <= 2) return;

        const horizontalGesture =
          Math.abs(event.deltaX) >
          Math.abs(event.deltaY);

        const shiftWheel =
          event.shiftKey &&
          Math.abs(event.deltaY) > 0;

        if (
          !horizontalGesture &&
          !shiftWheel
        ) {
          return;
        }

        const delta = horizontalGesture
          ? event.deltaX
          : event.deltaY;

        const previousScrollLeft =
          title.scrollLeft;

        title.scrollLeft += delta;

        if (
          title.scrollLeft !== previousScrollLeft
        ) {
          event.preventDefault();
          scheduleTitleScrollState(title);
        }
      },
      { passive: false }
    );

    title.addEventListener(
      "scroll",
      () => scheduleTitleScrollState(title),
      { passive: true }
    );

    scheduleTitleScrollState(title);
  }

  function bindTitleTouchAreas(scope = document) {
    scope.querySelectorAll(TITLE_TOUCH_SELECTOR).forEach(bindTitleTouchArea);
  }

  function refreshTitleScrollStates(scope = document) {
    scope.querySelectorAll(TITLE_SCROLL_SELECTOR).forEach(scheduleTitleScrollState);
  }

  function initAll(scope = document) {
    initThemeBlocks(scope);
    bindScrollStates(scope);
    bindTitleTouchAreas(scope);
    refreshInitialMobileScrollStates(scope);
    refreshTitleScrollStates(scope);
    initAutomaticReprints(scope);
  }

  function prepareUserScrollState(element) {
    const block = element.closest(BLOCK_SELECTOR);
    if (!block) return;

    refreshScrollStates(block);
  }

  document.addEventListener(
    "click",
    (event) => {
      const tab = event.target.closest(TAB_SELECTOR);
      if (!tab) return;

      const block = tab.closest(BLOCK_SELECTOR);
      if (!block) return;

      const targetId = tab.getAttribute("data-target") || tab.getAttribute("aria-controls");
      if (!targetId) return;

      event.preventDefault();

      activateTab(block, targetId, {
        resetRails: true,
        refreshScroll: true
      });
    },
    true
  );

  document.addEventListener(
    "keydown",
    (event) => {
      const tab = event.target.closest(TAB_SELECTOR);
      if (!tab) return;

      const block = tab.closest(BLOCK_SELECTOR);
      if (!block) return;

      const tabs = Array.from(block.querySelectorAll(TAB_SELECTOR));
      const currentIndex = tabs.indexOf(tab);

      if (currentIndex < 0) return;

      let nextIndex = currentIndex;

      if (event.key === "ArrowRight") {
        nextIndex = (currentIndex + 1) % tabs.length;
      } else if (event.key === "ArrowLeft") {
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      } else if (event.key === "Home") {
        nextIndex = 0;
      } else if (event.key === "End") {
        nextIndex = tabs.length - 1;
      } else {
        return;
      }

      const nextTab = tabs[nextIndex];
      const targetId = nextTab.getAttribute("data-target") || nextTab.getAttribute("aria-controls");

      if (!targetId) return;

      event.preventDefault();

      nextTab.focus({ preventScroll: true });

      activateTab(block, targetId, {
        resetRails: true,
        refreshScroll: true
      });
    },
    true
  );

  document.addEventListener(
    "pointerover",
    (event) => {
      const target = event.target.closest(`${TABS_SELECTOR}, ${SLIDER_SELECTOR}`);
      if (!target) return;

      prepareUserScrollState(target);
    },
    { passive: true }
  );

  document.addEventListener(
    "touchstart",
    (event) => {
      const target = event.target.closest(`${TABS_SELECTOR}, ${SLIDER_SELECTOR}`);
      if (!target) return;

      prepareUserScrollState(target);
    },
    { passive: true }
  );

  document.addEventListener(
    "focusin",
    (event) => {
      const target = event.target.closest(`${TABS_SELECTOR}, ${SLIDER_SELECTOR}`);
      if (!target) return;

      prepareUserScrollState(target);
    }
  );

  window.addEventListener(
    "resize",
    () => {
      if (resizeFrame) return;

      resizeFrame = requestAnimationFrame(() => {
        resizeFrame = null;

        document.querySelectorAll(BLOCK_SELECTOR).forEach((block) => {
          if (isMobileViewport()) {
            refreshScrollStates(block);
          }

          refreshTitleScrollStates(block);
        });
      });
    },
    { passive: true }
  );

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => {
        initAll();
      },
      { once: true }
    );
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initAll(event.target);
  });
})();
